import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import type { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import type { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import type { TradingSpreadKind } from '@ebalance/proto/bfftypes/v1/trading_spread_kind_pb'
import type { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import { listBilateralTrades } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service-SupplyBilateralTradeService_connectquery'
import {
  ListBilateralTradesRequestSchema,
  type ListBilateralTradesResponse,
} from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * フロントエンド表示用の取引行型。
 */
export type BilateralTradeRow = {
  id: string
  publicTradeId: string
  executionDate: ProtoDate | undefined
  executionRoute: TradingExecutionRoute
  partnerName: string
  buySell: Side
  areaCode: AreaCode
  deliveryPeriodFrom: ProtoDate | undefined
  deliveryPeriodTo: ProtoDate | undefined
  /** 受渡区分 (enum 固定値)。 旧 master id (UUID) から enum に変更 (delivery-type-enum-spec §6)。 */
  deliveryType: TradingDeliveryType
  timeZoneType: TradingTimeZoneType
  hasOption: boolean
  actualUtilizationRate: Decimal | undefined
  utilizationRateMin: Decimal | undefined
  utilizationRateMax: Decimal | undefined
  volumeMw: Decimal | undefined
  isManual: boolean
  /** optimistic lock 用に proto Timestamp で保持する。 表示時のみ timestampDate で Date 変換 */
  updatedAt: Timestamp | undefined
  updatedBy: string
}

export type ListBilateralTradesFilter = {
  buySellFilter?: Side
  executionRouteFilter?: TradingExecutionRoute
  timeZoneTypeFilter?: TradingTimeZoneType
  /** 受渡区分フィルタ (enum 固定値)。 旧 master id (UUID) から enum に変更 (delivery-type-enum-spec §6)。 */
  deliveryTypeFilter?: TradingDeliveryType
  areaCodeFilter?: AreaCode
  partnerNameFilter?: string
  deliveryPeriodFrom?: ProtoDate
  deliveryPeriodTo?: ProtoDate
  statusFilter?: TradingContractStatus
  spreadKindFilter?: TradingSpreadKind
  includeExpired?: boolean
  page?: number
  pageSize?: number
  /** false の間は取得しない (既定 true)。 */
  enabled?: boolean
}

/**
 * 一覧 query の定期再取得間隔 (AC-SBL-016)。取り込み途絶の判定 (import_staleness) は
 * 画面表示と独立した取り込み job の結果から backend が取得のたびに計算するため、
 * 一覧を開いたまま途絶したり復旧したりしてもバナーに反映されるよう定期的に取り直す
 * (app 既定は refetchOnWindowFocus: false)。途絶とみなす経過時間の既定 1h に対して
 * 十分短い間隔にする。
 */
export const BILATERAL_TRADES_REFETCH_INTERVAL_MS = 5 * 60 * 1000

export function useListBilateralTrades(
  filter: ListBilateralTradesFilter = {},
): UseQueryResult<ListBilateralTradesResponse, ConnectError> {
  const req = create(ListBilateralTradesRequestSchema, {
    buySellFilter: filter.buySellFilter,
    executionRouteFilter: filter.executionRouteFilter,
    timeZoneTypeFilter: filter.timeZoneTypeFilter,
    deliveryTypeFilter: filter.deliveryTypeFilter,
    areaCodeFilter: filter.areaCodeFilter,
    partnerNameFilter: filter.partnerNameFilter,
    deliveryPeriodFrom: filter.deliveryPeriodFrom,
    deliveryPeriodTo: filter.deliveryPeriodTo,
    statusFilter: filter.statusFilter,
    spreadKindFilter: filter.spreadKindFilter,
    // backend は statusFilter と includeExpired を AND 適用する (契約: trading_contract_repository.go)。
    // status=EXPIRED を選んだのに includeExpired=false だと EXPIRED 行が必ず除外され空集合になるため、
    // 「期間満了」 絞り込み時は includeExpired を強制する (= 無効な組み合わせを request 境界で抑止)。
    includeExpired:
      (filter.includeExpired ?? false) ||
      filter.statusFilter === TradingContractStatus.EXPIRED,
    page: filter.page ?? 1,
    pageSize: filter.pageSize ?? 50,
  })

  return useQuery(listBilateralTrades, req, {
    throwOnError: false,
    enabled: filter.enabled ?? true,
    refetchInterval: BILATERAL_TRADES_REFETCH_INTERVAL_MS,
  })
}
